import React, { useState } from 'react';
import {
  MonitorX,
  ShieldAlert,
  Tv,
  Smartphone,
  Laptop,
  Clock,
  ArrowLeft,
  Loader2,
  Users,
  Play,
  CheckCircle2,
} from 'lucide-react';
import { useIPTV } from '../../context/IPTVContext';
import { enableScreenWakeLock } from '../../utils/screenWakeLock';

export const ScreenLimitModal: React.FC = () => {
  const {
    screenLimitBlocked,
    screenLimitMessage,
    screenLimitScreens,
    screenLimitMaxScreens,
    disconnectScreen,
    resumePendingPlayback,
    closeScreenLimitModal,
  } = useIPTV();

  const [disconnectingDeviceId, setDisconnectingDeviceId] = useState<string | null>(null);

  if (!screenLimitBlocked) return null;

  const maxAllowed = screenLimitMaxScreens || 2;
  const isSlotFree = screenLimitScreens.length < maxAllowed;

  const getDeviceIcon = (deviceName?: string) => {
    const lower = (deviceName || '').toLowerCase();
    if (lower.includes('tv') || lower.includes('box')) {
      return <Tv className="w-4 h-4 text-emerald-400" />;
    }
    if (lower.includes('celular') || lower.includes('iphone') || lower.includes('android')) {
      return <Smartphone className="w-4 h-4 text-blue-400" />;
    }
    return <Laptop className="w-4 h-4 text-purple-400" />;
  };

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-xl animate-in fade-in duration-300 select-none">
      <div className="relative w-full max-w-lg max-h-[85vh] overflow-y-auto bg-zinc-950 border border-red-500/40 rounded-3xl p-4 sm:p-6 md:p-8 shadow-2xl shadow-red-950/60 space-y-6">
        {/* Glow Effect */}
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-64 h-64 bg-red-600/20 rounded-full blur-3xl pointer-events-none" />

        {/* Header Icon & Badges */}
        <div className="text-center space-y-3">
          <div className="relative mx-auto w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/30 flex items-center justify-center text-red-400 shadow-xl shadow-red-500/10">
            <MonitorX className="w-8 h-8 animate-pulse" />
            <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-600 text-white font-black text-[11px] flex items-center justify-center shadow">
              !
            </span>
          </div>

          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 text-xs font-black uppercase tracking-wider">
            <ShieldAlert className="w-3.5 h-3.5" />
            <span>Limite de Telas Simultâneas</span>
          </div>

          <h2 className="text-xl md:text-2xl font-black text-white tracking-wide">
            Número total de telas atingido
          </h2>

          <p className="text-xs md:text-sm text-zinc-300 leading-relaxed max-w-md mx-auto">
            {screenLimitMessage ||
              'Aumente seu plano ou desconecte uma das telas abaixo para iniciar a reprodução imediatamente:'}
          </p>
        </div>

        {/* Vaga Liberada Alert (apenas se liberada externamente) */}
        {isSlotFree && (
          <div className="p-3.5 rounded-2xl bg-emerald-950/80 border border-emerald-500/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-emerald-300 animate-in fade-in zoom-in-95 shadow-lg shadow-emerald-950/50">
            <div className="flex items-center gap-2 text-xs font-bold min-w-0">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              <span className="truncate">Vaga disponível! Você já pode assistir.</span>
            </div>
            <button
              type="button"
              onClick={() => {
                enableScreenWakeLock();
                resumePendingPlayback();
              }}
              className="w-full sm:w-auto shrink-0 px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 active:scale-95 text-black font-black text-xs flex items-center justify-center gap-1.5 transition shadow-lg shadow-emerald-900/40 cursor-pointer"
            >
              <Play className="w-3.5 h-3.5 fill-current" />
              <span>Assistir Agora</span>
            </button>
          </div>
        )}

        {/* Active Screens List com Desconexão e Abertura Instantânea */}
        <div className="space-y-3">
          <div className="flex items-center justify-between text-xs text-zinc-400 px-1 font-semibold">
            <span className="flex items-center gap-1.5 text-zinc-300">
              <Users className="w-4 h-4 text-red-400" />
              Telas ativas em reprodução ({screenLimitScreens.length}/{maxAllowed}):
            </span>
          </div>

          {screenLimitScreens.length > 0 ? (
            <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
              {screenLimitScreens.map((screen, idx) => {
                const idKey = screen.device_id || String(screen.id);
                const isThisDisconnecting = disconnectingDeviceId === idKey;

                return (
                  <div
                    key={idKey || idx}
                    className="bg-zinc-900/90 hover:bg-zinc-900 border border-zinc-800 rounded-2xl p-3.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-zinc-800/80 border border-zinc-700/60 flex items-center justify-center shrink-0">
                        {getDeviceIcon(screen.device_name)}
                      </div>
                      <div className="min-w-0 text-left">
                        <div className="text-sm font-bold text-white truncate flex items-center gap-2">
                          <span>{screen.profile_name || 'Perfil'}</span>
                        </div>
                        <div className="text-[11px] text-zinc-400 flex items-center gap-2 truncate">
                          <span>{screen.device_name || 'Dispositivo'}</span>
                          <span>•</span>
                          <span className="flex items-center gap-1 text-zinc-400">
                            <Clock className="w-3 h-3 text-amber-400/80" />
                            {screen.minutes_watching ? `${screen.minutes_watching} min` : 'Ativo'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      disabled={disconnectingDeviceId !== null}
                      onClick={async () => {
                        enableScreenWakeLock();
                        setDisconnectingDeviceId(idKey);
                        await disconnectScreen(screen.device_id, screen.id);
                        setDisconnectingDeviceId(null);
                      }}
                      className="w-full sm:w-auto shrink-0 px-3.5 py-2.5 rounded-xl bg-red-600 hover:bg-red-500 active:scale-95 text-white font-bold text-xs flex items-center justify-center gap-1.5 transition shadow-md shadow-red-900/30 cursor-pointer disabled:opacity-50"
                      title="Desconectar este aparelho e iniciar sua reprodução imediatamente"
                    >
                      {isThisDisconnecting ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          <span>Conectando...</span>
                        </>
                      ) : (
                        <>
                          <Play className="w-3.5 h-3.5 fill-current" />
                          <span>Desconectar e Assistir</span>
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-4 text-center text-xs text-zinc-400">
              Nenhuma outra tela em reprodução ativa no momento.
            </div>
          )}
        </div>

        {/* Action Buttons: Apenas Voltar ao Catálogo (sem botões repetidos) */}
        <div className="pt-2">
          <button
            type="button"
            onClick={closeScreenLimitModal}
            className="w-full py-3.5 px-4 rounded-xl bg-zinc-800 hover:bg-zinc-700 active:scale-98 text-white font-bold text-xs transition flex items-center justify-center gap-2 cursor-pointer border border-zinc-700/80 shadow-md"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Voltar ao Catálogo</span>
          </button>
        </div>
      </div>
    </div>
  );
};
